import { useMemo, useState, useRef, useCallback } from "react";
import { Tooltip } from "@/components/ds";
import { colorForName } from "@/domain/colors";
import type { Shift } from "@/domain/shifts/types";
import styles from "./TeamPanel.module.css";

function timeToMinutes(time: string): number {
  const [h, m] = time.split(":").map(Number);
  return h * 60 + m;
}

function formatDuration(totalMinutes: number): string {
  const h = Math.floor(totalMinutes / 60);
  const m = totalMinutes % 60;
  if (totalMinutes === 0) return "0h";
  if (m === 0) return `${h}h 0m`;
  return `${h}h ${m}m`;
}

interface Cleaner {
  readonly membershipId: string;
  readonly name: string;
  readonly pending: boolean;
}

interface Site {
  readonly id: string;
  readonly name: string;
}

interface TeamPanelProps {
  readonly cleaners: readonly Cleaner[];
  readonly shifts: readonly Shift[];
  readonly sites: readonly Site[];
  readonly weekStart: string;
  readonly highlightedCleaner: string | null;
  readonly pinnedCleaner: string | null;
  readonly onHighlight: (membershipId: string | null) => void;
  readonly onPin: (membershipId: string) => void;
  readonly onClose: () => void;
}

interface CleanerStats {
  readonly membershipId: string;
  readonly name: string;
  readonly pending: boolean;
  readonly shiftCount: number;
  readonly totalMinutes: number;
  readonly daysWorking: readonly boolean[];
  readonly siteNames: readonly string[];
}

const DAY_SHORT = ["M", "T", "W", "T", "F", "S", "S"] as const;

function dayIndex(date: string, weekStart: string): number {
  const d = new Date(date + "T12:00:00").getTime();
  const w = new Date(weekStart + "T12:00:00").getTime();
  return Math.round((d - w) / (1000 * 60 * 60 * 24));
}

export function TeamPanel({
  cleaners,
  shifts,
  sites,
  weekStart,
  highlightedCleaner,
  pinnedCleaner,
  onHighlight,
  onPin,
  onClose,
}: TeamPanelProps) {
  const [search, setSearch] = useState("");
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const dragRef = useRef<{ startX: number; startY: number; originX: number; originY: number } | null>(null);

  const onPointerDown = useCallback((e: React.PointerEvent) => {
    if ((e.target as HTMLElement).closest("button, input")) return;
    e.preventDefault();
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
    dragRef.current = { startX: e.clientX, startY: e.clientY, originX: offset.x, originY: offset.y };
  }, [offset]);

  const onPointerMove = useCallback((e: React.PointerEvent) => {
    if (!dragRef.current) return;
    const dx = e.clientX - dragRef.current.startX;
    const dy = e.clientY - dragRef.current.startY;
    setOffset({ x: dragRef.current.originX + dx, y: dragRef.current.originY + dy });
  }, []);

  const onPointerUp = useCallback(() => {
    dragRef.current = null;
  }, []);

  const siteMap = useMemo(() => {
    const map = new Map<string, string>();
    for (const site of sites) map.set(site.id, site.name);
    return map;
  }, [sites]);

  const stats = useMemo((): readonly CleanerStats[] => {
    const map = new Map<string, { count: number; minutes: number; days: boolean[]; siteIds: Set<string> }>();

    for (const cleaner of cleaners) {
      map.set(cleaner.membershipId, { count: 0, minutes: 0, days: [false, false, false, false, false, false, false], siteIds: new Set() });
    }

    for (const shift of shifts) {
      const existing = map.get(shift.membershipId as string);
      if (!existing) continue;
      const minutes = timeToMinutes(shift.endTime) - timeToMinutes(shift.startTime);
      existing.count++;
      existing.minutes += Math.max(0, minutes);
      existing.siteIds.add(shift.siteId as string);
      const di = dayIndex(shift.date, weekStart);
      if (di >= 0 && di < 7) existing.days[di] = true;
    }

    const result = cleaners.map((c) => {
      const s = map.get(c.membershipId) ?? { count: 0, minutes: 0, days: [false, false, false, false, false, false, false], siteIds: new Set<string>() };
      return {
        membershipId: c.membershipId,
        name: c.name,
        pending: c.pending,
        shiftCount: s.count,
        totalMinutes: s.minutes,
        daysWorking: s.days,
        siteNames: [...s.siteIds].map((id) => siteMap.get(id) ?? "").filter(Boolean),
      };
    });

    return [
      ...result.filter((s) => s.shiftCount > 0).sort((a, b) => b.totalMinutes - a.totalMinutes),
      ...result.filter((s) => s.shiftCount === 0),
    ];
  }, [cleaners, shifts, weekStart, siteMap]);

  const filtered = useMemo(() => {
    if (!search.trim()) return stats;
    const q = search.trim().toLowerCase();
    return stats.filter((s) => s.name.toLowerCase().includes(q));
  }, [stats, search]);

  if (cleaners.length === 0) return null;

  const panelStyle = (offset.x !== 0 || offset.y !== 0)
    ? { transform: `translate(${offset.x}px, ${offset.y}px)` } as React.CSSProperties
    : undefined;

  return (
    <aside className={styles.panel} aria-label="Team panel" style={panelStyle}>
      <div
        className={styles.header}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
      >
        <span className={styles.heading}>Team</span>
        <span className={styles.headerRight}>
          <span className={styles.count}>{cleaners.length}</span>
          <button
            type="button"
            className={styles.closeButton}
            onClick={onClose}
            aria-label="Hide team panel"
          >
            &times;
          </button>
        </span>
      </div>

      <input
        type="text"
        className={styles.search}
        placeholder="Search team"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
      />

      <ul className={styles.list}>
        {filtered.map((s) => {
          const color = colorForName(s.name);
          const isActive = highlightedCleaner === s.membershipId;
          const isPinned = pinnedCleaner === s.membershipId;
          const isUnassigned = s.shiftCount === 0;

          const sitesDisplay = s.siteNames.length > 3
            ? `${s.siteNames.slice(0, 3).join(", ")} +${s.siteNames.length - 3} more`
            : s.siteNames.join(", ");

          return (
            <li
              key={s.membershipId}
              className={`${styles.row} ${isUnassigned && !isPinned ? styles.rowUnassigned : ""}`}
              onMouseEnter={() => { if (!pinnedCleaner) onHighlight(s.membershipId); }}
              onMouseLeave={() => { if (!pinnedCleaner) onHighlight(null); }}
              onClick={() => onPin(s.membershipId)}
              tabIndex={0}
              onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onPin(s.membershipId); } }}
            >
              <div className={`${styles.rowHeader} ${isActive ? styles.rowHeaderActive : ""}`}>
                <span
                  className={styles.dot}
                  style={{ "--dot-color": color } as React.CSSProperties}
                />
                <span className={styles.nameCol}>
                  <span className={styles.name}>{s.name}</span>
                  {s.pending && (
                    <Tooltip content="Hasn't accepted their invite yet" delay={0}>
                      <span className={styles.pendingBadge}>Pending</span>
                    </Tooltip>
                  )}
                </span>
                <span className={styles.meta}>
                  {s.shiftCount} {s.shiftCount === 1 ? "shift" : "shifts"}
                </span>
                <span className={styles.hours}>
                  {formatDuration(s.totalMinutes)}
                </span>
              </div>
              {isPinned && (
                <div className={styles.expanded}>
                  {s.shiftCount > 0 ? (
                    <>
                      <div className={styles.days}>
                        {DAY_SHORT.map((d, i) => (
                          <span key={i} className={s.daysWorking[i] ? styles.dayActive : styles.dayInactive}>{d}</span>
                        ))}
                      </div>
                      {s.siteNames.length > 0 && (
                        <div className={styles.sitesList}>{sitesDisplay}</div>
                      )}
                    </>
                  ) : (
                    <div className={styles.sitesList}>No shifts this week</div>
                  )}
                </div>
              )}
            </li>
          );
        })}
      </ul>
    </aside>
  );
}
